import React, { FC, useContext, useEffect, useMemo } from 'react';
import DataConsumptionContext from '../../../../../contexts/Billing/DataConsumptionContext';
import DataConsumptionMetricContext from '../../../../../contexts/Billing/DataConsumptionMetricContext';
import { InfoPaper } from '../../../components/ConsumptionData/InfoPaper';
import { localization } from './localization';
import { useContentLocalizer } from '../../../../../hooks/useContentLocalizer';
import { formatDayMonthLong } from '../../../../../utils/date';
import { roundNextHundredth } from '../../../../../utils/numbers';
import { RightInfoPaperArea } from '../../styles';
import { ConsolidatedCenterPaper } from '../../../components/ConsumptionData/ConsolidatedPaperCenter';
import colors from '../../../../../_charts.module.scss';
import { BillingMetrics } from '../../../../../shared/BillingMetrics';
import { trackEvent } from '../../../../../api/Billing/Tracking';
import { REPORT_AI_TOKEN_CONSUMPTION_OPEN } from '../../../../../shared/SegmentEvents';

const AiTokenConsumptionComponent: FC = () => {
  const content = useContentLocalizer(localization) || localization.pt;
  const { dateToSearch, periodInfo, tenant, loggedUser, trackingProps } = useContext(DataConsumptionContext);
  const { dailyAiTokenConsumption, totalAiTokenConsumption, isAiTokenLoading, consolidatedAiTokenApiRequisition, containsDataOnSelectedMonth } =
    useContext(DataConsumptionMetricContext);

  useEffect(() => {
    consolidatedAiTokenApiRequisition(dateToSearch);
  }, [dateToSearch]);

  const accordionData = [
    {
      type: BillingMetrics.aiToken,
      title: content.header.modalContent.token.title,
      description: content.header.modalContent.token.description,
    },
  ];

  const header = {
    title: content.header.title,
    period: periodInfo,
    subtitle: content.header.description,
    learnMore: content.header.learnMore,
    modalContent: content.header.modalContent,
    buttonTitle: content.header.showMoreButton,
    pathname: content.header.pathname,
    hasModalWithAccordion: true,
    accordionData: accordionData,
    buttonTrackEvent: () => trackEvent(REPORT_AI_TOKEN_CONSUMPTION_OPEN, tenant, loggedUser, trackingProps),
  };

  const infoPaperArea = (
    <RightInfoPaperArea flexDirection="row">
      <InfoPaper
        justifyContent="flex-start"
        classNameTitle="flex-start-margin"
        title={
          containsDataOnSelectedMonth
            ? totalAiTokenConsumption.aiTokenTotal.toLocaleString(content.locale)
            : '0'
        }
        text={content.infoPaper.aiToken}
        hasCentralized={true}
      />
    </RightInfoPaperArea>
  );

  const infoPaperOptions = {
    headerTitle: content.infoPaper.headerTitle,
  };

  const dailyItems = (dailyAiTokenConsumption || []).slice().reverse();

  const dailyItemsLabel = useMemo(() => {
    return dailyItems.map((item) => formatDayMonthLong(item.dateReference, content.locale));
  }, [dailyItems, content.locale]);

  const anchoredDayIndexes = useMemo(() => {
    const anchorDays = new Set(['01', '08', '15', '22']);
    return new Set(
      dailyItemsLabel
        .map((label: string, index: number) => {
          const day = label.slice(0, 2);
          return anchorDays.has(day) ? index : -1;
        })
        .filter((index: number) => index >= 0),
    );
  }, [dailyItemsLabel]);

  const aiTokenData = dailyItems.map((item) => item.aiToken ?? 0);
  const maxDailyTotal = Math.max(...aiTokenData, 0) || 1;

  const chartData = {
    chartType: 'BarChart',
    title: content.chart.title,
    labels: dailyItemsLabel,
    customLabels: [
      {
        backgroundColor: colors.Brand,
        text: content.chart.aiToken,
      },
    ],
    datasets: [
      {
        label: content.chart.aiToken,
        data: aiTokenData,
        backgroundColor: colors.Brand,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    barThickness: 8,
    locale: content.locale,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value: number = context.parsed.y ?? 0;
            return `${content.chart.aiToken}: ${value.toLocaleString(content.locale)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          align: 'start' as const,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: (_value: any, index: number) => {
            return anchoredDayIndexes.has(index) ? dailyItemsLabel[index] : '';
          },
        },
      },
      y: containsDataOnSelectedMonth
        ? { min: 0, max: roundNextHundredth(maxDailyTotal * 1.1) }
        : { min: 0 },
    },
  };

  return (
    <ConsolidatedCenterPaper
      header={header}
      chart={{ chartData, chartOptions, redraw: false }}
      isLoading={isAiTokenLoading}
      infoPapers={{ ...infoPaperOptions, content: infoPaperArea }}
    />
  );
};

export default AiTokenConsumptionComponent;
